/**
 * Notification Translation Service
 *
 * Translates notification texts at render time based on user's language preference.
 * Uses text-to-key mapping to translate existing notifications without DB migration.
 */

import type { Language } from "../../i18n";
import type { INotificationTranslationService } from "./types";
import { TEXT_TO_KEY_REGISTRY, DYNAMIC_TEXT_PATTERNS } from "./text-to-key-registry";
import notificationsEn from "./notifications.en.json";
import notificationsNl from "./notifications.nl.json";

const translations: Record<Language, Record<string, unknown>> = {
  en: notificationsEn,
  nl: notificationsNl,
};

/**
 * Notification translation service implementation.
 * Stateless service that reads language from localStorage.
 */
export class NotificationTranslationService implements INotificationTranslationService {
  translateText(text: string, metadata?: Record<string, unknown>): string {
    const lang = this.getCurrentLanguage();

    // 1. Check exact match in registry
    const mapping = TEXT_TO_KEY_REGISTRY[text];
    if (mapping) {
      const params = mapping.extractParams?.(text, metadata) ?? {};
      return this.getTranslation(lang, mapping.key, params);
    }

    // 2. Check dynamic patterns (e.g., "Try again in 25s")
    for (const { pattern, key, extractParams } of DYNAMIC_TEXT_PATTERNS) {
      const match = text.match(pattern);
      if (match) {
        const params = extractParams(match);
        return this.getTranslation(lang, key, params);
      }
    }

    // 3. Fallback: return original text
    return text;
  }

  translateCta(ctaText: string): string {
    return this.translateText(ctaText);
  }

  translateToast(key: string, params?: Record<string, string>): string {
    const lang = this.getCurrentLanguage();
    return this.getTranslation(lang, key, params ?? {});
  }

  getCurrentLanguage(): Language {
    const lang = localStorage.getItem("language") ?? navigator.language;
    return lang === "nl" ? "nl" : "en";
  }

  private getTranslation(lang: Language, key: string, params: Record<string, string>): string {
    const langTranslations = translations[lang] ?? translations.en;
    const value = this.getNestedValue(langTranslations, key.split("."));

    if (typeof value !== "string") {
      // Missing translation - return key for debugging
      return `[${key}]`;
    }

    // Substitute params: {paramName} → value
    let result = value;
    for (const [param, val] of Object.entries(params)) {
      result = result.replaceAll(`{${param}}`, val);
    }
    return result;
  }

  private getNestedValue(obj: Record<string, unknown>, path: string[]): unknown {
    let current: unknown = obj;
    for (const key of path) {
      if (current === null || current === undefined) return undefined;
      current = (current as Record<string, unknown>)[key];
    }
    return current;
  }
}
